export type SolutionId = "yonda" | "magneto" | "turnalar";
export type SolutionTheme = SolutionId;

export type SolutionIconName =
  | "BadgeCheck"
  | "Boxes"
  | "Braces"
  | "Calculator"
  | "Code2"
  | "FileCheck2"
  | "Gauge"
  | "Globe2"
  | "Layers3"
  | "PlugZap"
  | "ReceiptText"
  | "RefreshCw"
  | "Rocket"
  | "SearchCheck"
  | "ShieldCheck"
  | "ShoppingBag"
  | "Sparkles"
  | "TrendingUp"
  | "Workflow";

interface SolutionItem {
  title: string;
  description: string;
  icon: SolutionIconName;
}

interface SolutionModule {
  kind: "steps" | "cards";
  eyebrow: string;
  title: string;
  description: string;
  items: readonly SolutionItem[];
}

export interface SolutionProvider {
  id: SolutionId;
  name: string;
  logo: {
    src: string;
    width: number;
    height: number;
  };
  route: string;
  theme: SolutionTheme;
  category: string;
  cardDescription: string;
  cardCapabilities: readonly string[];
  outcome: string;
  accentWords: string;
  description: string;
  heroBenefits: readonly string[];
  primaryCta: {
    label: string;
    href: string;
  };
  secondaryCta: {
    label: string;
    href: string;
  };
  capabilitiesTitle: string;
  capabilitiesDescription: string;
  capabilities: readonly SolutionItem[];
  reasonsTitle: string;
  reasons: readonly SolutionItem[];
  featureModule: SolutionModule;
  conversionTitle: string;
  conversionDescription: string;
  buddifyBenefits: readonly SolutionItem[];
  closingTitle: string;
  closingDescription: string;
  metadata: {
    title: string;
    description: string;
  };
}

function partnerReferralUrl(href: string, content: "cta" | "website") {
  const url = new URL(href);
  const attribution = {
    utm_source: "buddify.app",
    utm_medium: "referral",
    utm_campaign: "partners",
    utm_content: content,
  };
  for (const [key, value] of Object.entries(attribution)) {
    // Preserve attribution already agreed with a partner.
    if (!url.searchParams.has(key)) url.searchParams.set(key, value);
  }
  return url.toString();
}

export const solutions = {
  yonda: {
    id: "yonda",
    name: "Yonda Tax",
    logo: {
      src: "https://eradpbjcxfgjnxsfuszc.supabase.co/storage/v1/object/public/hq/website/solution-partners/logos/yonda-tax.svg",
      width: 700,
      height: 219,
    },
    route: "/partners/yonda-tax",
    theme: "yonda",
    category: "Tax Compliance Made Simple",
    cardDescription:
      "Global sales tax and VAT support, with free nexus tracking to keep your business compliant as it grows.",
    cardCapabilities: [
      "VAT & sales tax compliance",
      "Free nexus threshold tracking",
      "Global coverage",
    ],
    outcome: "Global Tax Compliance.",
    accentWords: "Made Simple.",
    description:
      "Registration, calculation, filing and remittance managed by tax experts and supported by technology.",
    heroBenefits: [
      "Global VAT & sales tax compliance",
      "Free Shopify nexus app",
      "Real-time tax calculations",
      "Reporting and audit support",
    ],
    primaryCta: {
      label: "Get Tax Assessment",
      href: partnerReferralUrl("https://www.yondatax.com/book-a-consultation?utm_source=Referral&utm_medium=Landing+Page&utm_campaign=Buddify", "cta"),
    },
    secondaryCta: {
      label: "Website",
      href: partnerReferralUrl("https://www.yondatax.com/", "website"),
    },
    capabilitiesTitle: "Tax compliance for every market you sell in",
    capabilitiesDescription:
      "A managed path from understanding your obligations to staying current as your sales footprint changes.",
    capabilities: [
      {
        title: "VAT & sales tax compliance",
        description:
          "Registration, filing and remittance support across regions.",
        icon: "ReceiptText",
      },
      {
        title: "Real-time calculations",
        description:
          "Accurate tax calculations connected to your commerce stack.",
        icon: "Calculator",
      },
      {
        title: "Free Shopify nexus app",
        description:
          "Track when your brand approaches or crosses tax thresholds globally.",
        icon: "Gauge",
      },
      {
        title: "Audit support",
        description:
          "Clear records and expert help when authorities ask questions.",
        icon: "ShieldCheck",
      },
      {
        title: "Global coverage",
        description: "One partner for sales tax, VAT and GST requirements.",
        icon: "Globe2",
      },
      {
        title: "Connected workflows",
        description: "Bring Shopify and other sales data into one process.",
        icon: "PlugZap",
      },
    ],
    reasonsTitle: "Why Buddify merchants choose Yonda",
    reasons: [
      {
        title: "Specialist introduction",
        description: "A direct route to a team focused on indirect tax.",
        icon: "BadgeCheck",
      },
      {
        title: "Managed service",
        description: "Experts handle the recurring compliance workload.",
        icon: "Workflow",
      },
      {
        title: "Built for commerce",
        description: "Support shaped around multichannel ecommerce operations.",
        icon: "ShoppingBag",
      },
      {
        title: "Clear next steps",
        description: "Start with an assessment of where action is required.",
        icon: "SearchCheck",
      },
    ],
    featureModule: {
      kind: "steps",
      eyebrow: "HOW IT WORKS",
      title: "Simple. Managed. Compliant.",
      description:
        "A practical path from connected sales data to ongoing filings.",
      items: [
        {
          title: "Connect",
          description: "Bring your Shopify and sales data into scope.",
          icon: "PlugZap",
        },
        {
          title: "Assess",
          description: "Identify where your business has tax obligations.",
          icon: "SearchCheck",
        },
        {
          title: "Set up",
          description: "Complete the required registrations and workflows.",
          icon: "FileCheck2",
        },
        {
          title: "Stay compliant",
          description: "Keep calculations, filings and remittance moving.",
          icon: "RefreshCw",
        },
      ],
    },
    conversionTitle: "Ready to simplify tax compliance?",
    conversionDescription:
      "Start with an assessment and understand the markets that need attention.",
    buddifyBenefits: [
      {
        title: "Warm introduction",
        description: "Reach the right Yonda specialist without starting cold.",
        icon: "Sparkles",
      },
      {
        title: "Shared context",
        description: "Explain your Shopify setup once and move forward faster.",
        icon: "Layers3",
      },
      {
        title: "Direct support",
        description: "Keep a clear path back to both specialist teams.",
        icon: "BadgeCheck",
      },
      {
        title: "Growth ready",
        description: "Build compliance into expansion plans from the start.",
        icon: "Rocket",
      },
    ],
    closingTitle: "Stay compliant. Sell everywhere.",
    closingDescription:
      "Take tax administration off your growth team’s weekly list.",
    metadata: {
      title: "Global Tax Compliance — Yonda Tax",
      description:
        "Explore managed sales tax and VAT compliance plus free global nexus threshold tracking from Yonda Tax for Shopify merchants.",
    },
  },
  magneto: {
    id: "magneto",
    name: "Magneto IT Solutions",
    logo: {
      src: "https://eradpbjcxfgjnxsfuszc.supabase.co/storage/v1/object/public/hq/website/solution-partners/logos/magneto-it-solutions.svg",
      width: 136,
      height: 64,
    },
    route: "/partners/magneto-it-solutions",
    theme: "magneto",
    category: "Shopify Development & Integration",
    cardDescription:
      "Custom Shopify development, migrations and system integrations built around your operating model.",
    cardCapabilities: [
      "Shopify Plus development",
      "Custom apps & integrations",
      "End-to-end delivery",
    ],
    outcome: "Your Shopify Vision.",
    accentWords: "Expertly Built.",
    description:
      "Custom Shopify storefronts, apps, migrations and enterprise integrations designed to support long-term growth.",
    heroBenefits: [
      "Shopify development specialists",
      "End-to-end project delivery",
      "Apps, integrations & migrations",
      "Shopify Plus capabilities",
    ],
    primaryCta: {
      label: "Book a Consultation",
      href: partnerReferralUrl("https://magnetoitsolutions.com/services/shopify-website-development?utm_source=buddify.app", "cta"),
    },
    secondaryCta: {
      label: "Website",
      href: partnerReferralUrl("https://magnetoitsolutions.com/", "website"),
    },
    capabilitiesTitle: "How Magneto helps you build and grow",
    capabilitiesDescription:
      "From a focused storefront project to a connected commerce platform, the work stays tied to your business goals.",
    capabilities: [
      {
        title: "Custom Shopify development",
        description:
          "Storefront experiences tailored to your brand and buyers.",
        icon: "Code2",
      },
      {
        title: "Shopify Plus implementation",
        description: "Scalable builds for complex and enterprise operations.",
        icon: "ShoppingBag",
      },
      {
        title: "Migrations & upgrades",
        description: "Structured replatforming with continuity in mind.",
        icon: "RefreshCw",
      },
      {
        title: "ERP & system integrations",
        description:
          "Connect commerce with the systems your team already uses.",
        icon: "Boxes",
      },
      {
        title: "App development",
        description: "Purpose-built capabilities for workflows that need more.",
        icon: "Braces",
      },
      {
        title: "Ongoing optimization",
        description: "Iterate on performance, conversion and operations.",
        icon: "Gauge",
      },
    ],
    reasonsTitle: "Why Buddify merchants choose Magneto",
    reasons: [
      {
        title: "Specialist introduction",
        description: "A direct line to a Shopify-focused delivery team.",
        icon: "BadgeCheck",
      },
      {
        title: "Full-lifecycle work",
        description: "Strategy, build, integration and ongoing support.",
        icon: "Workflow",
      },
      {
        title: "Connected commerce",
        description: "Storefront and operational systems planned together.",
        icon: "PlugZap",
      },
      {
        title: "Growth-minded delivery",
        description: "Technical decisions stay tied to commercial outcomes.",
        icon: "TrendingUp",
      },
    ],
    featureModule: {
      kind: "cards",
      eyebrow: "PROJECT PATHS",
      title: "Build what your next stage needs",
      description:
        "Choose the delivery path that best matches your current platform and growth plans.",
      items: [
        {
          title: "Storefront rebuild",
          description:
            "A stronger buying experience with a maintainable foundation.",
          icon: "Layers3",
        },
        {
          title: "Migration & integration",
          description:
            "Move platforms and connect the systems behind the store.",
          icon: "Boxes",
        },
        {
          title: "Ongoing optimization",
          description: "Improve performance, conversion and team workflows.",
          icon: "TrendingUp",
        },
      ],
    },
    conversionTitle: "Ready to build something better?",
    conversionDescription:
      "Share the project, systems and growth goal you are working toward.",
    buddifyBenefits: [
      {
        title: "Warm introduction",
        description: "Reach the relevant delivery team without starting cold.",
        icon: "Sparkles",
      },
      {
        title: "Clear brief",
        description: "Frame the project around the outcome and current stack.",
        icon: "FileCheck2",
      },
      {
        title: "Direct handoff",
        description: "Keep communication between both teams straightforward.",
        icon: "Workflow",
      },
      {
        title: "Long-term view",
        description: "Plan the build for what comes after launch.",
        icon: "Rocket",
      },
    ],
    closingTitle: "Build the next version of your commerce stack.",
    closingDescription:
      "Start with the business problem, then shape the right Shopify delivery path.",
    metadata: {
      title: "Shopify Development & Integration — Magneto IT Solutions",
      description:
        "Explore Shopify development, migrations, apps and enterprise integrations from Magneto IT Solutions.",
    },
  },
  turnalar: {
    id: "turnalar",
    name: "Turnalar",
    logo: {
      src: "/images/partners/turnalar.png",
      width: 3000,
      height: 584,
    },
    route: "/partners/turnalar",
    theme: "turnalar",
    category: "Digital Marketing & Growth",
    cardDescription:
      "Paid media, SEO, analytics and retention marketing for your next stage of ecommerce growth.",
    cardCapabilities: [
      "Paid search & social campaigns",
      "SEO & conversion optimization",
      "Analytics & email marketing",
    ],
    outcome: "Connect Your Marketing.",
    accentWords: "Focus Your Growth.",
    description:
      "Turnalar brings acquisition, measurement and retention together, from campaign strategy to conversion optimization.",
    heroBenefits: [
      "Performance marketing",
      "Search visibility",
      "Ecommerce analytics",
      "Customer retention",
    ],
    primaryCta: {
      label: "Book a Discovery Call",
      href: partnerReferralUrl("https://calendly.com/korkmaz-turnalar/discovery-audit", "cta"),
    },
    secondaryCta: {
      label: "Website",
      href: partnerReferralUrl("https://turnalar.co/", "website"),
    },
    capabilitiesTitle: "Support across the customer journey",
    capabilitiesDescription:
      "Connect the channels that attract customers with the experiences that bring them back.",
    capabilities: [
      {
        title: "Paid search & social",
        description: "Campaigns across Google, Meta, LinkedIn and other channels.",
        icon: "Rocket",
      },
      {
        title: "Search engine optimization",
        description: "Technical SEO, keyword research and content optimization.",
        icon: "SearchCheck",
      },
      {
        title: "Analytics & reporting",
        description: "Ecommerce tracking and Looker Studio dashboards.",
        icon: "Gauge",
      },
      {
        title: "Email & retention",
        description: "Lifecycle campaigns and email automation for repeat business.",
        icon: "RefreshCw",
      },
      {
        title: "Conversion optimization",
        description: "UX reviews and A/B testing across the buying journey.",
        icon: "TrendingUp",
      },
      {
        title: "CRM & automation",
        description: "Connect customer data, segmentation and marketing workflows.",
        icon: "Workflow",
      },
    ],
    reasonsTitle: "Find the right focus for your marketing",
    reasons: [
      {
        title: "Reach new customers",
        description: "Explore paid and organic acquisition together.",
        icon: "Globe2",
      },
      {
        title: "Understand performance",
        description: "Use tracking and reporting to guide decisions.",
        icon: "Gauge",
      },
      {
        title: "Improve the journey",
        description: "Look beyond the click to the storefront experience.",
        icon: "ShoppingBag",
      },
      {
        title: "Build retention",
        description: "Plan communication beyond the first purchase.",
        icon: "RefreshCw",
      },
    ],
    featureModule: {
      kind: "cards",
      eyebrow: "GROWTH PRIORITIES",
      title: "Where does your store need support?",
      description: "Start with the challenge that matters most to your business.",
      items: [
        {
          title: "Acquisition",
          description: "Paid media and SEO to reach relevant audiences.",
          icon: "Rocket",
        },
        {
          title: "Measurement",
          description: "Tracking and reporting to understand what is happening.",
          icon: "Gauge",
        },
        {
          title: "Retention",
          description: "Email and CRM to support lasting customer relationships.",
          icon: "RefreshCw",
        },
      ],
    },
    conversionTitle: "What is your next growth priority?",
    conversionDescription:
      "Discuss your channels, goals and current challenges with Turnalar.",
    buddifyBenefits: [
      {
        title: "Choose your focus",
        description: "Identify the marketing support your store needs.",
        icon: "SearchCheck",
      },
      {
        title: "Prepare your context",
        description: "Bring your goals, current channels and store setup.",
        icon: "FileCheck2",
      },
      {
        title: "Contact the specialist",
        description: "Book directly with Turnalar through its discovery calendar.",
        icon: "BadgeCheck",
      },
      {
        title: "Ask Buddify",
        description: "Contact our team for help choosing a solution.",
        icon: "Sparkles",
      },
    ],
    closingTitle: "Give your next stage a clear marketing focus.",
    closingDescription: "Start a conversation about your store’s growth priorities.",
    metadata: {
      title: "Digital Marketing & Growth — Turnalar",
      description:
        "Explore Turnalar’s paid media, SEO, ecommerce analytics, email marketing and conversion optimization services for your Shopify business.",
    },
  },
} as const satisfies Record<SolutionId, SolutionProvider>;

export const solutionProviders = [
  solutions.yonda,
  solutions.magneto,
  solutions.turnalar,
] as const;

export const solutionHeaderConfigs = solutionProviders.map((provider) => ({
  id: `solution-${provider.id}`,
  name: provider.name,
  href: provider.route,
  theme: provider.theme,
  links: [{ name: "Overview", href: provider.route }],
  cta: {
    label: provider.primaryCta.label,
    href: provider.primaryCta.href,
    external: true,
  },
}));
